import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CopyIcon from 'svgs/referrals/CopyIcon';
import {
  MarketAccessory,
  MarketActivityIndicator,
  MarketButton,
  MarketButtonGroup,
  MarketInputText,
} from '@market/react';
import { environment } from 'utils/environment';
import ShareLinkModal from './ShareLinkModal';
import QrCodeModal from './QrCodeModal';
import { useGetForSquareBuyerPersonQuery } from 'store/query/api-extensions/rewardsRpcs';
import { openToast, ToastVariant } from 'store/toastSlice';
import { useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';
import {
  clickCopyLinkProperties,
  clickShareLinkProperties,
  clickQrCodeProperties,
} from 'services/tracking/cdp/events/referrals';

const ReferralLink: React.FC = () => {
  const { t } = useTranslation();
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const dispatch = useDispatch();

  const { data: rewardCode, isLoading: isGetForSquareBuyerLoading } =
    useGetForSquareBuyerPersonQuery();
  const getReferralLink = () => {
    const baseUrl = environment().isProduction
      ? 'squareup.com'
      : 'squareupstaging.com';
    return `${baseUrl}/refer/${rewardCode?.token}`;
  };

  const copyToClipboard = () => {
    const referralLink = getReferralLink();
    navigator.clipboard.writeText(referralLink);
    buyerportalCdpClient.track(
      'Copy Referral Link',
      clickCopyLinkProperties(referralLink)
    );
    dispatch(
      openToast({
        message: t('refer.copyLink.toast'),
        variant: ToastVariant.Success,
      })
    );
  };

  const handleSendReferral = async () => {
    if (isMobile && navigator.share && navigator.canShare) {
      const shareData = {
        text: t('refer.share.text'),
        title: t('refer.share.title'),
        url: `https://${getReferralLink()}`,
      };

      if (navigator.canShare(shareData)) {
        try {
          buyerportalCdpClient.track(
            'Click Share Link',
            clickShareLinkProperties('mobile')
          );
          await navigator.share(shareData);
          return;
        } catch {
          return; // Just let user share the link through email (desktop experience)
        }
      }
    }

    // If not mobile or share not available, open the modal
    buyerportalCdpClient.track(
      'Click Share Link',
      clickShareLinkProperties('desktop')
    );
    setShowShareLinkModal(true);
  };

  return (
    <>
      <div className="space-y-6">
        <h3 className="text-2xl mt-0 font-bold text-gray-900">
          {t('refer.title')}
        </h3>
        <p className="text-gray-700 text-base">{t('refer.text')}</p>
        {isGetForSquareBuyerLoading ? (
          <MarketActivityIndicator />
        ) : (
          <MarketInputText
            aria-label={getReferralLink()}
            className="mt-12"
            readonly
          >
            <label className="break-all">{getReferralLink()}</label>
            <MarketButton
              size="small"
              iconOnly={true}
              rank="tertiary"
              slot="trailing-accessory"
              aria-label={t('refer.copyLink.button')}
              onClick={copyToClipboard}
            >
              <MarketAccessory slot="icon">
                <CopyIcon />
              </MarketAccessory>
            </MarketButton>
          </MarketInputText>
        )}
        <MarketButtonGroup className="mt-5" alignment="left">
          <MarketButton onClick={handleSendReferral} rank="primary">
            {t('refer.sendReferral.button')}
          </MarketButton>
          <MarketButton
            onClick={() => {
              buyerportalCdpClient.track(
                'Click QR Code Button',
                clickQrCodeProperties()
              );
              setShowQrCodeModal(true);
            }}
            rank="secondary"
          >
            {t('refer.openQrCode')}
          </MarketButton>
        </MarketButtonGroup>
      </div>
      {showShareLinkModal && rewardCode?.id !== undefined && (
        <ShareLinkModal
          onClose={() => setShowShareLinkModal(false)}
          rewardCodeId={rewardCode.id}
        />
      )}
      {showQrCodeModal && (
        <QrCodeModal
          onClose={() => setShowQrCodeModal(false)}
          inviteLink={getReferralLink()}
        />
      )}
    </>
  );
};

export default ReferralLink;
